<template>
  <section
    aria-label="header"
    :class="[
      'relative w-full transition-[top]',
      { 'sticky z-40': isHeaderVisible && isScrolledPastHeader },
      isHeaderVisible ? 'top-0' : '-top-full',
    ]"
    role="banner"
  >
    <div class="header relative z-40 w-full border-b border-b-[--color-white] bg-[color:var(--color-header-bottom-bg)]">
      <!-- region Default slot -->
      <transition>
        <div v-if="customSlots.default">
          <component :is="customSlots.default" />
        </div>

        <div v-else class="relative z-50 flex h-16 w-full items-center justify-between gap-x-2 sm:gap-x-6">
          <!-- region Left slot -->
          <component :is="customSlots.left" v-if="customSlots.left" />

          <div v-else class="flex h-full items-center">
            <!-- trigger menu icon -->
            <div class="flex h-full items-center justify-center pe-3 ps-4">
              <button
                v-if="!mobileMenuVisible"
                ref="mobileMenuButton"
                type="button"
                class="flex size-10 items-center justify-center rounded border border-[color:var(--color-neutral-a2)]"
                :aria-label="$t('shared.layout.header.top_header.open_menu')"
                @click="handleOpen"
              >
                <VcIcon class="text-[--color-neutral-a2]" name="menu" :size="30" />
              </button>

              <button
                v-else
                ref="mobileMenuButton"
                type="button"
                class="flex size-10 items-center justify-center rounded border border-[color:var(--color-white)]"
                :aria-label="$t('shared.layout.header.top_header.close_menu')"
                @click="closeMenu"
              >
                <VcIcon class="text-[--color-neutral-a2]" name="menu-close" :size="20" />
              </button>
            </div>
            <router-link to="/" class="max-w-40">
              <VcImage
                :src="logo?.url"
                :alt="$context.storeName"
                :width="150"
                :height="24"
                class="h-6 min-w-full"
                loading="eager"
                fallback-src="/static/thorlabs/logo.svg"
              />
            </router-link>
          </div>
          <!-- endregion Left slot -->

          <!-- region Right slot -->
          <component :is="customSlots.right" v-if="customSlots.right" />

          <div v-else class="flex h-full flex-row items-center pr-4">
            <PushMessages v-if="$cfg.push_messages_enabled && isAuthenticated" class="px-1 py-2 xs:px-2">
              <template #trigger="{ totalCount, unreadCount }">
                <div class="relative">
                  <transition :name="unreadCount ? 'shake' : ''" mode="out-in">
                    <VcIcon :key="totalCount" class="text-primary" name="bell" :size="28" />
                  </transition>

                  <transition mode="out-in" name="scale">
                    <VcBadge
                      v-if="unreadCount"
                      variant="outline"
                      size="sm"
                      class="absolute -right-2 -top-2 transition-transform"
                      rounded
                    >
                      {{ unreadCount }}
                    </VcBadge>
                  </transition>
                </div>
              </template>
            </PushMessages>

            <button
              ref="showSearchBarButton"
              type="button"
              class="flex px-2 py-6"
              :aria-label="$t('shared.layout.search_bar.enter_keyword_placeholder')"
              @click="toggleSearchBar"
            >
              <VcIcon
                name="search"
                class="size-4.5 text-[color:var(--color-neutral-a1)]"
                :class="searchBarOpen ? 'text-[color:var(--color-primary-a1)]' : 'text-[color:var(--color-neutral-a1)]'"
              />
            </button>
            <template v-if="isAuthenticated">
              <!-- Authenticated user account dropdown -->
              <button
                ref="showAccountButton"
                type="button"
                class="flex px-2"
                :aria-label="$t('common.links.account')"
                @click="toggleAccount"
              >
                <VcIcon
                  class="size-5"
                  name="user-filled"
                  :class="accountOpen ? 'text-[color:var(--color-primary-a1)]' : 'text-[color:var(--color-neutral-a1)]'"
                />
              </button>
              <!-- Do not remove this comment - replace with this code when the search is displayed- top-[calc(100%+75px)] -->
              <div
                v-show="accountOpen"
                ref="accountOpenElement"
                class="divide-stroke absolute right-0 top-[calc(100%+1px)] w-[200px] divide-y overflow-hidden border border-b-[3px] border-[color:var(--color-neutral-a4)] border-b-[color:var(--color-primary-a1)] bg-white p-5"
              >
                <div class="flex flex-col text-base">
                  <p class="text-dark break-words text-base font-semibold text-[color:var(--color-neutral-a2)]">
                    {{ $t(`${greeting}`) }}{{ isJapanese ? " " : ", " }}{{ greetingName }}{{ isJapanese ? "" : "!" }}
                  </p>
                  <div class="my-4 h-px w-full bg-[color:var(--color-neutral-a4)]"></div>
                  <div class="flex flex-col gap-4">
                    <a
                      v-if="!punchoutCookies"
                      href="/account/account-info"
                      class="text-dark flex w-full items-center justify-between text-base font-medium"
                      @click="accountOpen = false"
                    >
                      <span class="flex items-center gap-2"> {{ $t("pages.cart.my_account_title") }}</span>
                    </a>
                    <a
                      href="/account/orders"
                      class="text-dark flex w-full items-center justify-between text-base font-medium"
                      @click="accountOpen = false"
                    >
                      <span class="flex items-center gap-2">
                        {{ $t("shared.account.navigation.links.my_orders") }}
                      </span>
                    </a>
                    <a
                      v-if="!punchoutCookies"
                      href="/account/quotes"
                      class="text-dark flex w-full items-center justify-between text-base font-medium"
                      @click="accountOpen = false"
                    >
                      <span class="flex items-center gap-2">
                        {{ $t("shared.account.navigation.links.my_quotes") }}
                      </span>
                    </a>
                  </div>
                  <div v-if="!punchoutCookies" class="my-4 h-px w-full bg-[color:var(--color-neutral-a4)]"></div>
                  <div>
                    <button
                      v-if="!punchoutCookies"
                      type="button"
                      class="text-dark flex w-full items-center justify-between text-base font-medium"
                      @click="
                        () => {
                          signMeOut();
                          accountOpen = false;
                        }
                      "
                    >
                      <span class="flex items-center gap-2"> {{ $t("shared.layout.header.link_logout") }} </span>
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <button
                ref="showAccountButton"
                type="button"
                class="relative flex px-2 py-6 after:absolute after:left-0 after:top-full after:block after:h-8 after:w-full after:content-normal"
                :class="{
                  'hover:[&_svg]:text-primary ': accountOpen,
                }"
                :aria-label="$t('common.links.account')"
                @click="toggleAccount"
              >
                <VcIcon
                  class="text-[--color-neutral-a1]"
                  :class="{ 'text-primary': accountOpen }"
                  name="user-circle"
                  :size="20"
                />
              </button>
              <!-- Do not remove this comment - replace with this code when the search is displayed- top-[calc(100%+75px)] -->
              <div
                v-show="accountOpen"
                ref="accountOpenElement"
                class="absolute right-0 top-[calc(100%+1px)] w-full transition-all"
              >
                <div
                  class="border-b-[3px] border-t border-b-[color:var(--color-primary-a1)] border-t-[color:var(--color-neutral-a4)] bg-white p-8"
                >
                  <SignInForm @close-form="accountOpen = false" />
                </div>
              </div>
            </template>
            <router-link
              v-slot="{ isActive, href, navigate }"
              class="mini-cart mt-1 p-2"
              :to="cartLink?.route ?? ''"
              custom
            >
              <button type="button" class="flex px-2" @click="showDialogMiniCart">
                <span class="relative flex items-center">
                  <VcIcon
                    class="text-[--color-neutral-a1]"
                    name="icon-cart-shopping-md"
                    :aria-label="$t('common.labels.minicart')"
                    :size="20"
                  />

                  <transition
                    mode="out-in"
                    enter-from-class="scale-0"
                    leave-to-class="scale-0"
                    enter-active-class="will-change-transform"
                    leave-active-class="will-change-transform"
                  >
                    <VcBadge
                      v-if="totalItems"
                      variant="solid"
                      size="sm"
                      class="absolute -right-2 -top-2 transition-transform"
                      rounded
                    >
                      {{ $n(totalItems, "decimal", { notation: "compact" }) }}
                    </VcBadge>
                  </transition>
                </span>
              </button>
              <!-- Flyout-right -->
              <Dialog
                ref="dialogMiniCart"
                dialog-mode="medium"
                dialog-class="mobile-full minicart-dialog"
                content-class="dialog-content"
                is-flyout
                @close-dialog="handleCloseDialog"
              >
                <template #title>
                  <h3 class="m-0 text-base font-semibold">{{ $t("pages.cart.title") }}</h3>
                </template>

                <template #content>
                  <MiniCart
                    :href="href"
                    :navigate="navigate"
                    :is-active="isActive"
                    :link="cartLink"
                    @close-dialog="handleCloseDialog"
                  />
                </template>
              </Dialog>
            </router-link>
          </div>
          <!-- endregion Right slot -->
        </div>
      </transition>
      <!-- endregion Default slot -->

      <BannerAlertHeader
        v-if="bannerBlock && bannerBlock.position === 'Bottom'"
        :id="`bannerAlertHeader${bannerBlock.position}`"
        ref="bannerAlertHeaderRefBottom"
        :block="bannerBlock"
      />

      <!-- region Mobile Search Bar -->
      <div
        v-show="searchBarOpen"
        ref="searchBarOpenElement"
        class="mobile-search-bar absolute right-0 top-[calc(100%+1px)] w-full border-b-[3px] border-t border-b-[color:var(--color-primary-a1)] border-t-[color:var(--color-neutral-a4)] bg-white p-4 transition-all"
      >
        <AlgoliaAutocomplete
          @search-submitted="searchBarOpen = false"
          @item-selected="searchBarOpen = false"
        ></AlgoliaAutocomplete>
        <!-- <SearchBar role="menuitem"  /> -->
      </div>
      <!-- endregion Mobile Search Bar -->
    </div>
    <!-- Mobile menu -->
    <transition
      enter-from-class="-translate-y-full"
      leave-to-class="-translate-y-full"
      enter-active-class="will-change-transform"
      leave-active-class="will-change-transform"
    >
      <MobileMenu
        v-if="mobileMenuVisible"
        :mobile-menu-items="mobileMenuItems"
        class="transition-transform print:hidden"
        @close-menu="closeMenu"
      />
    </transition>
  </section>
</template>

<script setup lang="ts">
import { onClickOutside, whenever } from "@vueuse/core";
import { sumBy } from "lodash";
import { ref, onMounted, computed, watchEffect, onBeforeUnmount, watch, shallowRef } from "vue";
import { useRouteQueryParam, useNavigations } from "@/core/composables";
import { QueryParamName } from "@/core/enums";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { SignInForm, useSignMeOut } from "@/shared/account";
import { useLanguageCheck } from "@/shared/account/composables/useLanguageCheck";
import { useUser } from "@/shared/account/composables/useUser";
import { useFullCart, useShortCart } from "@/shared/cart";
import { useNestedMobileHeader, useSearchBar, AlgoliaAutocomplete } from "@/shared/layout";
import { eventBus } from "@/shared/thorlabs/shared";
import BannerAlertHeader from "./banner-alert-header.vue";
import MobileMenu from "./mobile-menu.vue";
import type { IBannerBlock, ILogo, IMobileMenuItem } from "@/shared/thorlabs/blocks";
import type { Ref } from "vue";
import MiniCart from "@/shared/cart/components/mini-cart.vue";
import Dialog from "@/shared/thorlabs/shared/components/dialog-modal.vue";

defineProps<IProps>();

const searchPhrase = ref("");
const searchPhraseInUrl = useRouteQueryParam<string>(QueryParamName.SearchPhrase);
const mobileMenuVisible = ref(false);

const { isJapanese } = useLanguageCheck();
const { isAuthenticated, greetingName, greeting } = useUser();
const { customSlots } = useNestedMobileHeader();
const { searchBarVisible } = useSearchBar();
const { cart } = useShortCart();
const { signMeOut } = useSignMeOut();
const { mobileMainMenuItems } = useNavigations();
const { forceFetch } = useFullCart();

interface IProps {
  logo?: ILogo;
  mobileMenuItems?: IMobileMenuItem[];
  bannerBlock?: IBannerBlock;
}
const showAccountButton = shallowRef<HTMLElement | null>(null);
const showSearchBarButton = shallowRef<HTMLElement | null>(null);
const accountOpen = ref(false);
const accountOpenElement = ref(null);
const mobileMenuButton = ref<HTMLElement | null>(null);
const searchBarOpen = ref(false);
const searchBarOpenElement = ref(null);

const dialogMiniCart = ref<InstanceType<typeof Dialog> | null>(null);
const showDialogMiniCart = () => {
  dialogMiniCart.value?.openDialog();
};

const cartLink = mobileMainMenuItems.value.find((x) => x.id?.toLocaleLowerCase() === "cart");

const handleCloseDialog = () => {
  dialogMiniCart.value?.closeDialog();
};
onClickOutside(
  accountOpenElement,
  () => {
    accountOpen.value = false;
  },
  { ignore: [showAccountButton] },
);
onClickOutside(
  searchBarOpenElement,
  () => {
    searchBarOpen.value = false;
  },
  { ignore: [showSearchBarButton, ".aa-Panel"] },
);

const toggleAccount = () => {
  accountOpen.value = !accountOpen.value;
};

const toggleSearchBar = () => {
  searchBarOpen.value = !searchBarOpen.value;
};

const handleOpen = () => {
  mobileMenuVisible.value = true;
};
const closeMenu = () => {
  mobileMenuVisible.value = false;
};

watchEffect(() => (searchPhrase.value = searchPhraseInUrl.value ?? ""));
whenever(searchBarVisible, () => (searchPhrase.value = searchPhraseInUrl.value ?? ""), { immediate: true });
const isHeaderVisible = ref(true);

const isAnyPanelOpen = computed(() => searchBarOpen.value || accountOpen.value || mobileMenuVisible.value);

const totalItems = computed(() => (cart.value ? sumBy(cart.value?.items, (x) => x.quantity) : 0));

const watchAndSetOverflow = (refToWatch: Ref<boolean>) => {
  watch(refToWatch, (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "auto";
    const mainElement = document.getElementById("app");
    if (newVal) {
      if (mainElement) {
        mainElement.classList.add("mobile-menu-open");
      }
    } else {
      if (mainElement) {
        mainElement.classList.remove("mobile-menu-open");
      }
    }
  });
};

// Use the function for both refs
watchAndSetOverflow(mobileMenuVisible);
watchAndSetOverflow(accountOpen);
watchAndSetOverflow(searchBarOpen);

const isScrollingUp = ref(false);
let lastScrollPosition = 0;
const isScrolledPastHeader = ref(false);
onMounted(async () => {
  try {
    await forceFetch();
  } catch {
    // Cart fetch failure should not prevent scroll listener from being registered
  }
  const handleScroll = () => {
    if (isAnyPanelOpen.value) {
      isHeaderVisible.value = true;
      return;
    }
    const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;
    const headerElement = document.querySelector('[aria-label="header"]');
    const headerHeight = (headerElement as HTMLElement)?.offsetHeight || 0;
    isScrollingUp.value = currentScrollPosition < lastScrollPosition;
    isScrolledPastHeader.value = currentScrollPosition > headerHeight;

    isHeaderVisible.value = isScrollingUp.value || currentScrollPosition < headerHeight;

    lastScrollPosition = currentScrollPosition;
  };

  window.addEventListener("scroll", handleScroll, false);
  eventBus.on("closeMenu", () => {
    closeMenu();
  });
  onBeforeUnmount(() => {
    window.removeEventListener("scroll", handleScroll, false);
  });
});

// const isActive = undefined;
// const href = undefined;
</script>

<style scoped lang="scss">
.header {
  transition: top 0.3s ease-in-out; // keep same animation as the tabNavBar
}
</style>
